import React, { useState } from 'react';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';

export function ProjectsView({
  projects,
  onAddProject,
  onUpdateProject,
  onDeleteProject
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', color: '#3b82f6' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', color: '' });

  const handleAdd = async () => {
    if (!newProject.name.trim()) return;
    await onAddProject(newProject.name, newProject.color);
    setNewProject({ name: '', color: '#3b82f6' });
    setShowAddForm(false);
  };

  const startEdit = (project) => {
    setEditingId(project.id);
    setEditForm({ name: project.name, color: project.color });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', color: '' });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return;
    await onUpdateProject(editingId, { name: editForm.name.trim(), color: editForm.color });
    cancelEdit();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Projects</h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
          >
            <Plus size={20} />
            Add Project
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">New Project</h3>
          <div className="flex gap-3">
            <input
              type="color"
              value={newProject.color}
              onChange={(e) => setNewProject({ ...newProject, color: e.target.value })}
              className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
            />
            <input
              type="text"
              value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              placeholder="Project name"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
            />
            <button
              onClick={handleAdd}
              disabled={!newProject.name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Check size={20} />
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewProject({ name: '', color: '#3b82f6' });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {projects.map((project) => {
          const isEditing = editingId === project.id;

          if (isEditing) {
            return (
              <div
                key={project.id}
                className="bg-white border-2 border-blue-500 rounded-lg p-4"
              >
                <div className="flex gap-3">
                  <input
                    type="color"
                    value={editForm.color}
                    onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                    className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Project name"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                    onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                  />
                  <button
                    onClick={saveEdit}
                    disabled={!editForm.name.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <Check size={20} />
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div
              key={project.id}
              className="bg-white border border-gray-200 rounded-lg p-4"
              style={{ borderLeftWidth: '4px', borderLeftColor: project.color }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded"
                    style={{ backgroundColor: project.color }}
                  />
                  <span className="font-medium text-gray-900">{project.name}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(project)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => onDeleteProject(project.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          No projects yet. Add your first project!
        </div>
      )}
    </div>
  );
}
